<template>
  <SfModalPortal
    modal-id="report-element-details-modal"
    :size="modalSizes.fullscreen"
    :close-on-escape="false"
    content-class-name="tw-flex-1 tw-min-h-0 !tw-p-0 !tw-overflow-hidden"
    @opened="onOpened"
    @close="onClose"
    @close-attempt="onCloseAttempt"
  >
    <template #title>
      <div class="tw-flex tw-flex-row tw-gap-2 tw-items-center">
        <div class="tw-flex tw-flex-col tw-gap-0.5">
          <span class="tw-text-2xl tw-font-medium tw-text-neutral-900">
            {{ elementName }}
          </span>
        </div>
        <span
          v-if="period"
          class="
            tw-rounded-full
            tw-bg-sf-snow-dark
            tw-text-sf-grey
            tw-text-xs
            tw-px-2
            tw-py-1.5
            tw-font-normal
          "
        >
          {{ period }}
        </span>
        <span v-if="elementValue" class="tw-text-xl tw-font-normal">
          {{ elementValue }}
        </span>
      </div>
    </template>

    <template #body>
      <div v-if="loading" class="tw-p-6">
        <RemarkSkeleton v-for="i in 4" :key="i" class="tw-my-1" />
      </div>
      <div
        v-else-if="errored"
        class="tw-flex tw-items-center tw-justify-center tw-p-6 tw-h-full"
      >
        <div class="tw-text-sm tw-text-sf-neutral-light-1 tw-text-center">
          {{ I18n.t('detailed_entry.load_error') }}
        </div>
      </div>
      <template v-else>
        <div
          class="
            tw-flex tw-flex-col
            lg:tw-flex-row
            tw-h-full tw-min-h-0 tw-overflow-y-auto
            lg:tw-overflow-hidden
          "
        >
          <!-- Left column: Chart -->
          <div
            class="
              tw-bg-slate-50
              tw-flex
              tw-flex-col
              tw-flex-1
              tw-min-w-0
              tw-min-h-0
              tw-overflow-hidden
              tw-rounded-md
            "
          >
            <div
              class="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-gap-6 tw-p-4"
            >
              <div
                class="
                  tw-bg-white
                  tw-p-4
                  tw-rounded-md
                  tw-border
                  tw-border-solid
                  tw-border-sf-snow-dark
                  tw-flex-1
                  tw-min-h-[300px]
                "
              >
                <div class="tw-relative tw-w-full tw-h-full">
                  <div
                    v-if="chartLoading"
                    class="
                      tw-absolute
                      tw-inset-0
                      tw-flex
                      tw-items-center
                      tw-justify-center
                    "
                  >
                    <span
                      class="
                        fa fa-spinner fa-spin
                        tw-text-2xl tw-text-sf-neutral-light-1
                      "
                    />
                  </div>
                  <div
                    ref="chartContainerRef"
                    class="tw-w-full tw-h-full"
                    :class="{ 'tw-invisible': chartLoading }"
                  />
                </div>
              </div>
              <div
                v-if="description"
                v-dompurify-html="description"
                class="
                  tw-bg-white
                  tw-p-4
                  tw-rounded-md
                  tw-border
                  tw-border-solid
                  tw-border-sf-snow-dark
                "
              />
            </div>
          </div>

          <!-- Right column: Top 5 & Messages -->
          <div
            class="
              tw-flex tw-flex-col tw-rounded-sm
              lg:tw-shrink-0 lg:tw-w-[400px] lg:tw-overflow-y-auto
            "
          >
            <!-- Top 5 -->
            <div v-if="top5.length" class="tw-p-4">
              <div
                class="
                  tw-text-sf-neutral-dark
                  tw-font-medium
                  tw-text-lg
                  tw-mb-3
                  tw-pb-2
                "
              >
                {{ I18n.t('detailed_entry.top5') }}
              </div>
              <table class="sf-table sf-table-border sf-full-width">
                <thead>
                  <tr>
                    <th>
                      <span>{{ I18n.t('detailed_entry.relation') }}</span>
                    </th>
                    <th class="sf-alignr">
                      <span>{{ I18n.t('detailed_entry.value') }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in top5" :key="index">
                    <td>
                      <div>{{ item.relation }}</div>
                      <div
                        v-if="item.relationOriginal"
                        class="tw-italic tw-text-sf-neutral"
                      >
                        {{ item.relationOriginal }}
                      </div>
                    </td>
                    <td class="sf-alignr">
                      {{ item.value }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Element Messages -->
            <div>
              <div class="tw-flex tw-items-center tw-justify-between tw-p-4">
                <div class="tw-text-sf-neutral-dark tw-font-medium tw-text-lg">
                  {{
                    presentationMode
                      ? I18n.t('detailed_entry.sidebar.meeting_messages')
                      : I18n.t('detailed_entry.sidebar.account_messages')
                  }}
                </div>
                <SfButtonDs
                  variant="primary"
                  class="tw-text-center"
                  @click="openRemarkModal"
                >
                  {{ I18n.t('communication.add_remark') }}
                </SfButtonDs>
              </div>
              <IsolatedSidepaneCommunication
                v-if="reportElementId && ledgerId"
                context-type="report/element"
                :context-id="reportElementId"
                :ledger-id="ledgerId"
                :show-empty-state="true"
              />
            </div>
          </div>
        </div>
      </template>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { modalSizes } from '@app/shared/vue/components/sf-modal/constants'
import { SfButtonDs } from '@silverfin/ui-components'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'

import { useElementTrendChart } from '@app/shared/echarts/use-element-trend-chart'
import IsolatedSidepaneCommunication from '@app/application/report/components/isolated-sidepane-communication.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import talkingPointRemarkPayload from '@app/application/report/utils/talking-point-remark-payload'
import reportDisplayParams from '@app/application/report/utils/report-display-params'

import type { ModalPortalPayloadMap } from '@app/types/index'

type ElementDetailsPayload =
  ModalPortalPayloadMap['report-element-details-modal']

interface Top5Item {
  relation: string
  relationOriginal?: string
  value: string
  rawValue: number
}

interface ElementDetailsResponse {
  name: string
  elementId: number
  reportTemplateId: number
  description: string | null
  top5: Top5Item[]
  chartUrls: {
    ytd: string | null
    ptd: string | null
  }
}

const I18n = useI18n()

const presentationMode = window.Silverfin.presentationMode

const loading = ref(false)
const chartLoading = ref(false)
const errored = ref(false)
const elementName = ref('')
const elementValue = ref('')
const period = ref('')
const reportElementId = ref<number | null>(null)
const ledgerId = ref<number | null>(null)
const reportTemplateId = ref<number | null>(null)
const top5 = ref<Top5Item[]>([])
const description = ref<string | null>(null)
const chartContainerRef = ref<HTMLElement | null>(null)

const { renderChart, disposeChart } = useElementTrendChart(chartContainerRef)

function onCloseAttempt() {
  if (!window.modalCitizen.hasOpenOverlayModal()) {
    window.modalCitizen.closeModalPortal()
  }
}

async function onOpened(payload: ElementDetailsPayload) {
  loading.value = true
  elementName.value = payload.title
  elementValue.value = payload.value || ''
  period.value = payload.period
  reportElementId.value = payload.reportElementId
  ledgerId.value = payload.ledgerId
  errored.value = false
  chartLoading.value = true
  top5.value = []

  const detailsUrl = buildElementDetailsUrl(
    payload.reportId,
    payload.ledgerId,
    payload.reportElementId
  )

  try {
    const data = await SFetch.json<ElementDetailsResponse>(detailsUrl)
    if (data) {
      reportTemplateId.value = data.reportTemplateId
      top5.value = data.top5 || []
      description.value = data.description

      loading.value = false
      await nextTick()

      await renderChart(data.chartUrls)
      chartLoading.value = false
    }
  } catch (error) {
    errored.value = true
    loading.value = false
    chartLoading.value = false
    SfLog.error('Failed to load report element details', {
      error,
      extra: {
        reportElementId: payload.reportElementId,
        reportId: payload.reportId,
        ledgerId: payload.ledgerId,
      },
    })
  }
}

function onClose() {
  loading.value = false
  chartLoading.value = false
  errored.value = false
  reportElementId.value = null
  ledgerId.value = null
  reportTemplateId.value = null
  top5.value = []
  description.value = null
  disposeChart()
}

function buildElementDetailsUrl(
  reportId: number,
  ledgerIdParam: number,
  elementId: number
): string {
  return window.Silverfin.Routes.report_element_detail_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    reportId,
    elementId,
    {
      format: 'json',
      ledger_id: ledgerIdParam,
      ...reportDisplayParams(),
    }
  )
}

function openRemarkModal() {
  window.modalCitizen.openModal('new-remark', {
    ownerType: 'Report::Template',
    ownerId: reportTemplateId.value,
    ledgerId: ledgerId.value,
    hidePeriodSelector: true,
    allRemarkTags: window.allRemarkTags || {},
    showTags: true,
    reportElementId: reportElementId.value,
    ...talkingPointRemarkPayload(presentationMode),
  })
}
</script>
